/**
 * experiments.js
 *
 * A simple tool for creating weighted experiments on the front end.
 * This is useful for A/B testing unauthenticated users who are served
 * cached pages instead of hitting our application server.
 *
 *
 * Usage
 * =====
 *
 * // First, instantiate an experiments instance. This will contain cached tests
 * // that have been previously registered.
 * var experiments = new Experiments();
 *
 * // Before the logic you wish to experiment on, register a test with it's
 * // weighted bucket values. If the user has already been bucketed (i.e. the user
 * // has previously visited this experience and has not cleared his/her localStorage
 * // cache), this will be ignored. This example registers an A/B test with generic
 * // buckets (named 'a' and 'b') weighted at 50% each.
 * experiments = register('slideview.cta.placement', 50);
 *
 * // Now you can implement bucket checks in your experience logic.
 * if (experiments.is('slideview.cta.placement', 'a') {
 *    $('#button-cta').addClass('experiment-a');
 * }
 *
 * // You can also use addClass to automagically apply CSS styles to elements
 * // based on a bucket value.
 * experiments.addClass('#container', 'slideview.cta.placement');
 *
 * // Then in your CSS:
 * #container.exp--slideview-cta-placement--a {
 *    display: none;
 * }
 *
 *
 * Whitelisting
 * ============
 *
 * // Before releasing experiments to the general public, you can whitelist specific
 * // tests to run for specific users by passing an extra parameter in your 'register' call.
 * // Currently, only supported group is:
 * //   'superuser': SlideShare super users
 * // On the horizon:
 * //   'linkedin': All LinkedIn users
 *
 * // For defining a single whitelisted group, use a single string:
 * experiments.register('slideview.cta.placement', 50, 'superuser');
 *
 * // For defining multiple groups, use an array:
 * experiments.register('slideview.cta.placement', 50, ['superuser', 'linkedin']);
 *
 *
 * More Examples
 * =============
 *
 * // Register a simple A/B test (argument is a number):
 * experiments.register('slideview.cta.placement', 50);
 *
 * // Register a more complex A/B/C test (argument is an array):
 * experiments.register('slideview.cta.placement', [33, 66]);
 *
 * // If you don't want to use the predefined generic bucket names ('a', 'b', 'c', etc.)
 * // you can provide your own using an object argument:
 * experiments.register('slideview.cta.placement', {
 *   'inside player': 33,
 *   'outside player': 66,
 *   'completely hidden': 100
 * });
 *
 * // Register a test with a preset bucket (argument is a string):
 * experiments.register('slideview.cta.placement', 'b');
 *
 * // Register a test that will only assign buckets to users in a provided whitelist.
 * experiments.register('slideview.cta.placement', 50, 'superuser');
 *
 * // Validating a bucket without defined names (from number or array arguments):
 * if (experiments.is('slideview.cta.placement', 'a') {
 *   // do something for bucket 'a'
 * }
 *
 * // Validating a bucket with defined names:
 * if (experiments.is('slideview.cta.placement', 'inside player') {
 *   // do something for bucket 'inside player'
 * }
 *
 */
;(function (win, localStorage) {
  // A reference to the utils helper object.
  let utils = win.slideshare_object.utils,
    // The Whitelist constructor object used the 'register' method.
    Whitelist = utils.imports("Whitelist")

  /**
   * Creates a new Experiments instance, unpacking experiment data from localStorage
   * to be used for matching test cases by bucket value.
   * @constructor
   */
  function Experiments() {
    let experiments = localStorage[Experiments.LS_NAMESPACE] && JSON.parse(localStorage[Experiments.LS_NAMESPACE])
    this._experiments = experiments || {}
  }

  Experiments.prototype = /** @lends Experiments.prototype */ {
    /**
     * Checks whether or not a bucket is assigned for an experiment. This is the primary method
     * for directing logic for registered experiments.
     * @param {string} key The experiment key to check.
     * @param {string} bucket The bucket value to check.
     * @returns {boolean} Is the bucket assigned for this user?
     */
    is: function (key, bucket) {
      return this.getBucket(key) === bucket
    },

    /**
     * Gets the experiment by key.
     * @param {string} key The experiment key.
     * @returns {Object} The experiment data.
     */
    getExperiment: function (key) {
      return this._experiments[key]
    },

    /**
     * Retrieves the bucket value set for a given test key.
     * @param {string} key The test key to retrieve the set bucket value from.
     * @returns {string} The set bucket value.
     */
    getBucket: function (key) {
      let experiment = this.getExperiment(key)
      return experiment && experiment.bucket
    },

    /**
     * Checks whether or not the experiment overrides whitelist privileges.
     * @param {string} key The experiment key.
     * @returns {boolean} Is this experiment set to override?
     */
    isOverride: function (key) {
      let experiment = this.getExperiment(key)
      return experiment && experiment.override
    },

    /**
     * Removes a set experiment by key from the internal experiments collection and localStorage.
     * @param {string} key The experiment to remove.
     * @returns {*} The result of '_save' (likely null).
     */
    deleteExperiment: function (key) {
      if (!this.isOverride(key)) {
        delete this._experiments[key]
        return this._save()
      }
      return null
    },

    /**
     * Sets a generated class on the referenced element which can be a raw DOM node or
     * a selector. The class is created based on the given test key and the key's bucket value.
     * This will be a standard format resembling the following example:
     *    exp--slideview-actions--b
     * Where the test key is "slideview actions" and the bucket value is "b"
     * @param {HTMLElement|string} el A reference to the element to modify.
     * @param {string} key The test key to base the modification on.
     * @returns {Experiments} A reference to this instance. Perhaps you'd like to chain these.
     */
    addClass: function (el, key) {
      let bucket = this.getBucket(key)
      if (bucket) {
        utils.addClass(el, "exp--" + utils.sanitizeForCSS(key) + "--" + utils.sanitizeForCSS(bucket))
      }
      return this
    },

    /**
     * Assigns a bucket value for a given experiment key and caches the result in localStorage.
     * Optionally, whitelist groups can be included which will register the test only for group members
     * (for instance, SS super users or all LI employees). This method is the primary way to set
     * experiment buckets to keep the experience as persistent as possible for users.
     * @param {string} key The experiment to register.
     * @param {string|number|array|object} buckets Weighted bucket values. See usage examples at the top of this file.
     * @param {string|array} [groups] User groups this experiment should be activated for. See Whitelist.GROUPS.
     * @returns {string} The registered bucket value.
     */
    register: function (key, buckets, groups) {
      // Create a whitelist and fetch the key's bucket value.
      let whitelist = new Whitelist(groups),
        bucket = this.getBucket(key)

      // If the whitelist is valid (i.e. the passed groups are valid), determine if the user is a member
      // and act accordingly.
      if (whitelist.isValid()) {
        // If the user is a member of the whitelist group, this test is enabled for them. If they don't
        // have a bucket assigned yet, assign one.
        if (whitelist.containsUser()) {
          if (!bucket) {
            return this._save(key, this._assign(buckets))
          }
        }

        // Otherwise, this test is disabled. If there's a bucket set (perhaps a different user on the same computer),
        // remove it.
        else {
          if (bucket) {
            return this.deleteExperiment(key)
          }
        }
      }

      // Otherwise, if the whitelist is invalid then the test is enabled. Assign a bucket unless one exists already.
      else if (!bucket) {
        return this._save(key, this._assign(buckets))
      }

      return bucket
    },

    /**
     * Sets a bucket value, overriding a previous value and ignoring whitelist checks.
     * This is useful for enabling experiments via bookmarklets (or other opt-in methods).
     * @param {string} key The experiment to override (or register if non-existent).
     * @param {string|number|array|object} buckets Weighted bucket values. See usage examples at the top of this file.
     * @returns {string} The registered bucket value.
     */
    override: function (key, buckets) {
      return this._save(key, this._assign(buckets), true)
    },

    /**
     * Wrapper for slideshare_object.ga call
     * Prepends the bucket value to the label sent in ga call, so that variants can be compared on ga
     * @param {string} key The experiment which is being tracked on ga
     * @param {string} category ga-category (name for the group of objects being tracked)
     * @param {string} action ga-action (uniquely paired with the category)
     * @param {|string|} label ga-label (additional dimension for the event)
     * @param {|number|} value ga-value (numeric data about the event)
     * @param {|boolean|} isNonInteractiveEvent not used in bounce-rate calculation in ga if true
     */
    ga: function (key, category, action, label, value, isNonInteractiveEvent) {
      let bucket = this.getBucket(key)
      if (bucket) {
        _gaq.push(["_trackEvent", category, action, bucket + "_" + label, value, !!isNonInteractiveEvent])
      }
      return null
    },

    /**
     * Generates and returns a bucket value for a given experiment.
     * @param {string|number|array|object} buckets Weighted bucket values.
     * @returns {string} The value assigned at random.
     */
    _assign: function (buckets) {
      // Convert numeric arguments to arrays.
      if (utils.isNumber(buckets)) {
        buckets = [buckets]
      }

      // Arrays are weights without named buckets. Invoke _assignFromArray and return the result.
      if (utils.isArray(buckets)) {
        return this._assignFromArray(buckets)
      }

      // If buckets is an object, it's probably a map of named buckets
      // and their corresponding values.
      else if (utils.isPlainObject(buckets)) {
        return this._assignFromObject(buckets)
      }

      // If buckets is a string, it's probably a bucket value.
      else if (utils.isString(buckets)) {
        return buckets
      }

      return null
    },

    /**
     * Selects a bucket value given an array of weighted values. This is the primary method for
     * generating a bucket value assignment.
     * @param {array} values A collection of weighted values up to 100.
     *                       If 100 is not included it will be appended automatically.
     * @param {array} [buckets=Experiments.BUCKETS] Bucket value names corresponding to the values array.
     * @returns {string} The bucket value assigned at random.
     * @private
     */
    _assignFromArray: function (values, buckets) {
      let ONE_HUNDRED = 100,
        rand = Math.random(),
        index,
        length

      // If no bucket values are passed, use generic values.
      buckets = buckets || Experiments.BUCKETS

      // Ensure the upper bound is 100 to complete the range of percents.
      if (values[values.length - 1] !== ONE_HUNDRED) {
        values.push(ONE_HUNDRED)
      }

      for (index = 0, length = values.length; index < length; index++) {
        if (rand <= values[index] / ONE_HUNDRED) {
          return buckets[index] || Experiments.BUCKETS[index]
        }
      }
      return null
    },

    /**
     * Selects a bucket value from an object keyed by weighted values with bucket names
     * as values. See the usage examples for register() at the top of this file.
     * @param {object} bucketsObj An object to be converted to two arrays for use in _assignFromArray().
     * @returns {string} The bucket value assigned from _assignFromArray().
     * @private
     */
    _assignFromObject: function (bucketsObj) {
      let buckets = [],
        values = [],
        key

      for (key in bucketsObj) {
        buckets.push(key)
        values.push(bucketsObj[key])
      }

      return this._assignFromArray(values, buckets)
    },

    /**
     * Persists the current _experiments object in localStorage and sets a particular experiment key
     * to a specified value if these arguments are passed.
     * @param {string} key The test key to set.
     * @param {string} value The bucket value to set.
     * @param {[boolean]} override Should this bucket value persist outside of whitelists?
     * @returns {string|null} The value of the successfully set key, or null or no value was passed.
     * @private
     */
    _save: function (key, value, override) {
      let experiments = this._experiments
      value = value || null

      if (key) {
        experiments[key] = {
          bucket: value,
        }
        if (override) {
          experiments[key].override = true
        }
      }

      localStorage[Experiments.LS_NAMESPACE] = JSON.stringify(this._experiments)

      return value
    },
  }

  // A list of generic bucket names to use if none are provided.
  Experiments.BUCKETS = "a b c d e f".split(" ")

  // The localStorage namespace that contains experiments data.
  Experiments.LS_NAMESPACE = "slideshare.experiments"

  // Export the Experiments object.
  utils.exports("Experiments", Experiments)
})(window, window.localStorage || {})
